import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { extractApiErrorMessage } from "../lib/apiError";
import { Input } from "./ui/Input";

export default function AuthModal({ isOpen, onClose }) {
  const id = useId();
  const navigate = useNavigate();
  const { login } = useAuth();

  // Login fields
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Reset transient state whenever the modal (re)opens
  useEffect(() => {
    if (isOpen) {
      setLoginError("");
    }
  }, [isOpen]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      await login(loginEmail, loginPassword);
      onClose();
      navigate("/home", { replace: true });
    } catch (err) {
      // `detail` can be a string, a 422 list, or a structured license object —
      // rendering it raw threw "Objects are not valid as a React child" and
      // blanked the page through the ErrorBoundary.
      setLoginError(extractApiErrorMessage(err, "Authentication failed"));
    } finally {
      setLoginLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="auth-modal-backdrop" onClick={handleBackdropClick}>
      <div className="auth-modal-card" role="dialog" aria-modal="true">
        {/* Close button */}
        <button type="button" className="auth-modal-close" onClick={onClose} aria-label="Close">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path d="M1 1L14 14M14 1L1 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className="auth-modal-top">
          <h2 className="auth-modal-title">Welcome back</h2>
          <p className="auth-modal-subtitle">Enter your credentials to sign in to your account.</p>
        </div>

        {/* Login form */}
        <form className="auth-modal-form" onSubmit={handleLogin} noValidate>
          <div className="auth-modal-field">
            <label htmlFor={`${id}-email`}>Email</label>
            <Input
              id={`${id}-email`}
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
            />
          </div>
          <div className="auth-modal-field">
            <div className="auth-modal-field-row">
              <label htmlFor={`${id}-password`}>Password</label>
              <button
                type="button"
                className="auth-modal-forgot"
                onClick={() => {}}
              >
                Forgot password?
              </button>
            </div>
            <Input
              id={`${id}-password`}
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          {loginError && <p className="auth-modal-error">{loginError}</p>}
          <button type="submit" className="auth-modal-submit" disabled={loginLoading}>
            {loginLoading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
